import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { FulfillService } from '../../fulfill.service';
import { takeUntil } from 'rxjs/operators';
import { PopoverService } from '@storybook/services/popover/popover.service';
import { PdqUser } from '@pdqdev/pdq-shared/dist/entities';
import { UserService } from '@app/services/user/user.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { OrderBatch } from '@services/order-batch/order-batch.model';

enum FilterBy {
  CARRIERS = 'carriers',
  LOCATIONS = 'locations',
  OWNERS = 'owners',
}

interface FilterOptions {
  displayName: string;
  active: boolean;
  options: { active: boolean; value: string | null; label: string }[];
}

@Component({
  selector: 'fulfill-batch-list-fulfillment-ready',
  templateUrl: './fulfill-batch-list-fulfillment-ready.component.html',
  styleUrls: ['./fulfill-batch-list-fulfillment-ready.component.scss'],
  standalone: false,
})
export class FulfillBatchListFulfillmentReadyComponent implements OnChanges, OnDestroy, OnInit {
  @ViewChild('userCollaboration') userCollaboration: TemplateRef<any>;

  private destroy$ = new Subject();
  @Input() batches;
  @Input() columns;
  @Input() pdqUsersList: PdqUser[];
  FilterBy = FilterBy;
  filteredBatches;
  carriers: FilterOptions;
  locations: FilterOptions;
  owners: FilterOptions;
  activeFilters: Partial<Record<FilterBy, string>> = {};
  filterText;
  constructor(
    private fulfillService: FulfillService,
    private userService: UserService,
    private popoverService: PopoverService,
    private cdRef: ChangeDetectorRef,
  ) {
    this.fulfillService.onHasFilter.pipe(takeUntil(this.destroy$)).subscribe((filterText) => {
      this.filterText = filterText;
      this.filterBatches();
    });
  }

  ngOnInit() {
    this.setOwnerOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.batches?.currentValue?.length) {
      this.setCarriersOptions(changes.batches.currentValue);
      this.setLocationsOptions(changes.batches.currentValue);
      this.filterBatches();
    }
  }

  setOwnerOptions() {
    this.owners = {
      displayName: 'Owner',
      active: false,
      options: [{ active: false, value: null, label: 'All' }],
    };
    this.userService.users$.pipe(takeUntil(this.destroy$)).subscribe((users) => {
      if (!users?.length) {
        return;
      }

      users.forEach((user) => {
        const ownerOption = {
          active: false,
          value: user.email,
          label: user.name,
          img: {
            path: user.profilePictureUrl,
            class: 'avatar',
          },
        };

        this.owners.options.push(ownerOption);
      });
    });
  }

  setCarriersOptions(batches) {
    this.carriers = {
      displayName: 'Carrier',
      active: false,
      options: [{ active: false, value: null, label: 'All' }],
    };
    batches.forEach((batch) => {
      const carrierExists = this.carriers.options?.map((c) => c.value).includes(batch.selectedCarrier.id);
      if (!carrierExists) {
        this.carriers?.options?.push({
          active: false,
          label: batch.selectedCarrier.name,
          value: batch.selectedCarrier.id,
        });
      }
    });
  }

  setLocationsOptions(batches) {
    this.locations = {
      displayName: 'Location',
      active: false,
      options: [{ active: false, value: null, label: 'All' }],
    };
    batches.forEach((batch) => {
      const location = batch.shipFromAddress?.storeName;
      if (location && !location?.length) return;

      const locationExists = this.locations?.options?.map((c) => c.value).includes(location);
      if (!locationExists && location) {
        this.locations?.options.push({
          active: false,
          label: location,
          value: location,
        });
      }
    });
  }

  setBatchFilter(filter: FilterBy, event) {
    const value = event.value;

    if (value) {
      this.activeFilters[filter] = value;
    } else {
      delete this.activeFilters[filter];
    }

    this.filterBatches();
  }

  filterBatches() {
    const filterFunctionMap: {
      [key in FilterBy]?: (batch: any, value: any) => boolean;
    } = {
      [FilterBy.CARRIERS]: (batch, value) => batch.selectedCarrier.id === value,
      [FilterBy.LOCATIONS]: (batch, value) => batch.shipFromAddress?.storeName === value,
      [FilterBy.OWNERS]: (batch, value) => batch.userCollaboration.some((user) => user.id === value),
    };

    let result = this.batches;

    for (const [filterKey, value] of Object.entries(this.activeFilters)) {
      const filterFn = filterFunctionMap[filterKey as FilterBy];
      if (filterFn && value) {
        result = result.filter((batch) => filterFn(batch, value));
      }
    }

    this.popoverService.setState(true);

    result = this.filterByText(result, this.filterText);
    result = this.filterEmptyBatches(result);
    this.filteredBatches = result;
  }

  get hasActiveFilters(): boolean {
    return Object.keys(this.activeFilters).length > 0;
  }

  trackByFn(index, batch) {
    return batch.id;
  }

  async onDroped(event: CdkDragDrop<string[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.batches, event.previousIndex, event.currentIndex);
    this.filterBatches();
    this.cdRef.detectChanges();
    this.cdRef.markForCheck();

    this.batches.forEach((b, idx) => (b.priority = idx));

    this.fulfillService.setBatchesPriority(this.batches);
  }

  filterByText(batches, text) {
    if (text && text.length && batches.length) {
      return batches.filter((batch) => batch.name.toLowerCase().includes(text.toLowerCase()));
    }
    return batches;
  }

  filterEmptyBatches(batches: OrderBatch[]) {
    return batches.filter((batch) => batch.orders.length > 0);
  }

  ngOnDestroy(): void {
    this.carriers = null;
    this.owners = null;
    this.locations = null;
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
